import { ArcVoiceOrbState } from '../../components/ArcVoice/ArcVoiceOrb'
import { ArcVoiceCallStatus, ArcVoicePauseReason, IArcVoiceCallSnapshot } from './arcVoiceCall'
import { ArcVoiceActivity } from './realtimeVoiceClient'

/** Who has the floor decides the orb: talking beats working, working beats waiting. */
export const orbStateFor = ({ status, activity, isArcWorking }: IArcVoiceCallSnapshot) => {
  if (status === ArcVoiceCallStatus.Paused || status === ArcVoiceCallStatus.Ended) {
    return ArcVoiceOrbState.Paused
  }
  if (status === ArcVoiceCallStatus.Connecting) return ArcVoiceOrbState.Connecting
  if (activity === ArcVoiceActivity.Speaking) return ArcVoiceOrbState.Speaking
  if (activity === ArcVoiceActivity.Hearing) return ArcVoiceOrbState.Hearing
  if (isArcWorking) return ArcVoiceOrbState.Thinking
  return ArcVoiceOrbState.Listening
}

const PAUSE_LABELS: Record<ArcVoicePauseReason, string> = {
  [ArcVoicePauseReason.User]: 'Paused',
  [ArcVoicePauseReason.Idle]: 'Paused after a quiet spell',
  [ArcVoicePauseReason.Limit]: 'Paused, ready for a fresh line',
  [ArcVoicePauseReason.Disconnected]: 'Line dropped',
}

const ORB_LABELS: Record<ArcVoiceOrbState, string> = {
  [ArcVoiceOrbState.Connecting]: 'Connecting…',
  [ArcVoiceOrbState.Listening]: 'Listening',
  [ArcVoiceOrbState.Hearing]: 'Listening to you…',
  [ArcVoiceOrbState.Thinking]: 'Arc is working on it…',
  [ArcVoiceOrbState.Speaking]: 'Arc is speaking',
  [ArcVoiceOrbState.Paused]: 'Paused',
}

/** One line under the orb: what is happening, with Arc's current step while it works. */
export const callStatusLabel = (snapshot: IArcVoiceCallSnapshot): string => {
  if (snapshot.status === ArcVoiceCallStatus.Paused) {
    return PAUSE_LABELS[snapshot.pauseReason ?? ArcVoicePauseReason.User]
  }
  const isRedialling =
    snapshot.status === ArcVoiceCallStatus.Connecting &&
    snapshot.pauseReason === ArcVoicePauseReason.Disconnected
  if (isRedialling) return 'Reconnecting…'
  const state = orbStateFor(snapshot)
  if (state === ArcVoiceOrbState.Listening && snapshot.isMuted) return 'Muted · Arc can’t hear you'
  if (state === ArcVoiceOrbState.Thinking && snapshot.progress) return snapshot.progress
  return ORB_LABELS[state]
}

/** A call that hasn't hung up yet. */
export const isCallOpen = (
  snapshot: IArcVoiceCallSnapshot | null,
): snapshot is IArcVoiceCallSnapshot => !!snapshot && snapshot.status !== ArcVoiceCallStatus.Ended

/** A call still running with no stage on screen, so the pill and its working items stand in. */
export const isRunningOffStage = (
  snapshot: IArcVoiceCallSnapshot | null,
  isStageVisible: boolean,
): snapshot is IArcVoiceCallSnapshot => !isStageVisible && isCallOpen(snapshot)
